import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Spin,
  Row,
  Col,
  Modal,
  message,
  Tooltip,
  Typography,
} from 'antd';
import {
  QuestionCircleOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import API from 'utils/api';
import fetchSome from 'utils/fetchSome';
import { onError, onSuccess } from 'utils/handlers';
import NumericInput from 'components/NumericInput';
import Select from 'components/Select';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import { SET_TIPOS_VIALIDADES } from 'store/reducers/catalogs';
import { getCatalogoDePaises, getCodigosPostales } from 'api/catalogos';
import { getModelPermission } from 'utils/permission';
import address from 'utils/address';
import BusquedaCodigoPostal from './Direccion/BusquedaCodigoPostal';

const { Text } = Typography;
const { TextArea } = Input;

const ModalDireccion = ({
  parentID,
  disabled,
  callback,
  currentURI,
  form: _form,
  onClick,
  _ref,
  addressName,
  required,
  isEditing,
}) => {
  const permission = useMemo(() => getModelPermission('direccion'), []);
  const tiposVialidades = useSelector(({ catalogs }) => catalogs.tiposVialidades);
  const [formDireccion] = Form.useForm();
  const form = _form || Form.useFormInstance();
  const [direccionStr, setDireccionStr] = useState();
  const [direccionObj, setDireccionObj] = useState();
  const [visibleDireccion, setVisibleDireccion] = useState(false);
  const [visibleNoSeCodigo, setVisibleNoSeCodigo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPartial, setLoadingPartial] = useState(false);
  const [filteredColonies, setFilteredColonies] = useState([]);
  const [selected, setSelected] = useState();
  const [paisTemp, setPaisTemp] = useState({ results: [] });
  const paises = useRef({ results: [] });
  // const colonies = useRef({ results: [] });
  const [params, setParams] = useState(null);

  const formatValues = (values) => {
    const keys = Object.keys(values);
    const clone = {};
    keys.forEach((prop) => {
      const value = values[prop];
      if (value && value !== 'N/A') {
        clone[prop] = value;
      }
    });
    return clone;
  };

  const getDireccion = async (formValues = null) => {
    try {
      if (formValues) {
        const codigo_postal = filteredColonies.find((e) => e.id === formValues.codigo_postal);
        const formattedValues = formatValues({
          ...formValues,
          estado: codigo_postal.municipio.estado.nombre_de_AGEE || undefined,
          municipio: codigo_postal.municipio.nom_mun || undefined,
          codigo_postal: codigo_postal.d_codigo,
          colonia: codigo_postal.id,
        });
        formDireccion.setFieldsValue(formattedValues);
        setDireccionObj(formattedValues);
        setDireccionStr(address(formattedValues));
      } else {
        setLoadingPartial(true);
        let uri = currentURI;
        if (currentURI[currentURI.length - 1] !== '/') {
          uri = `${uri}/`;
        }
        const resParent = (await API.get(`${uri}${parentID}/`)).data;
        if (resParent[addressName]) {
          const data = resParent[addressName];
          const resColonies = await API.get('/catalogos/codigos-postales/', {
            params: {
              d_codigo: data.codigo_postal.d_codigo,
            },
          });
          const formattedValues = formatValues({
            ...data,
            estado: data.codigo_postal.municipio.estado.nombre_de_AGEE || undefined,
            municipio: data.codigo_postal.municipio.nom_mun || undefined,
            codigo_postal: data.codigo_postal.d_codigo,
            colonia: data.codigo_postal.id,
          });
          setParams({ d_codigo: parseInt(data.codigo_postal.d_codigo, 10) });
          setFilteredColonies(resColonies.data || []);
          setDireccionObj(formattedValues);
          setDireccionStr(address(data));
          formDireccion.setFieldsValue(formattedValues);
        } else {
          formDireccion.resetFields();
        }
        setLoadingPartial(false);
      }
    } catch (err) {
      onError(err, setLoadingPartial);
    }
  };

  useEffect(() => {
    formDireccion.setFieldsValue({ direccionStr: direccionStr || 'Sin dirección' });
    // eslint-disable-next-line
  }, [direccionStr]);

  useEffect(() => {
    if (parentID) {
      getDireccion();
    } else {
      setDireccionStr();
    }
  }, [parentID]);

  const onCancel = () => {
    formDireccion.resetFields();
    if (isEditing) {
      setDireccionStr();
      setDireccionObj();
      setParams();
    }
    if (direccionObj && !isEditing) {
      setTimeout(() => {
        formDireccion.setFieldsValue(direccionObj);
      });
    }
    setVisibleDireccion(false);
  };

  const onFinish = async () => {
    try {
      const formValues = formDireccion.getFieldsValue();
      const values = {
        ...formValues,
        codigo_postal: formValues.colonia,
      };
      delete values.estado;
      delete values.municipio;
      delete values.pais;
      delete values.colonia;
      if (parentID) {
        let uri = currentURI;
        if (currentURI[currentURI.length - 1] !== '/') {
          uri = `${uri}/`;
        }
        setLoadingPartial(true);
        const resPatch = await API.patch(`${uri}${parentID}/`, {
          [addressName]: values,
        });
        setLoadingPartial(false);
        if ([200, 201].includes(resPatch?.status)) {
          setVisibleDireccion(false);
          setTimeout(() => getDireccion());
          callback(resPatch[addressName]);
          onSuccess(resPatch, 'Dirección modificada correctamente');
          onCancel();
        }
      } else {
        callback(values);
        setTimeout(() => getDireccion());
        form.setFieldsValue({ [addressName]: JSON.stringify(values) });
        formDireccion.setFieldsValue({ [addressName]: JSON.stringify(values) });
        setVisibleDireccion(false);
      }
    } catch (err) {
      onError(err, setLoadingPartial);
    }
  };

  const fetch = async () => {
    try {
      if (!tiposVialidades || !tiposVialidades.length) {
        fetchSome('/usuarios/tipo-vialidad/', SET_TIPOS_VIALIDADES);
      }
    } catch (err) {
      onError(err, setLoadingPartial);
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      const _paises = await getCatalogoDePaises({ id: 152 });
      if (mounted) {
        setPaisTemp(_paises);
      }
    };
    if (mounted) {
      fetch();
      fetchAll();
      formDireccion.setFieldsValue({ pais: 152 });
    }

    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  let timeout = null;
  const getFromPostalCode = (codigo) => {
    try {
      const d_codigo = String(codigo || '').trim();
      setLoading(true);
      const { pais } = formDireccion.getFieldsValue();
      clearTimeout(timeout);
      if (d_codigo.length === 5 && parseInt(d_codigo, 10)) {
        setParams({ d_codigo: parseInt(d_codigo, 10) });
        timeout = setTimeout(async () => {
          setLoading(true);
          const res = await API.get('/catalogos/codigos-postales/', {
            params: { d_codigo },
          });
          const { results } = res?.data;
          const resultsFromCountry = results.filter((e) => e.municipio.pais === pais);
          if (resultsFromCountry.length) {
            const [firstItem] = res.data.results;
            if (res.data.results.length === 1) {
              formDireccion.setFieldsValue({
                codigo_postal: firstItem.d_codigo,
                estado: firstItem.municipio.estado.nombre_de_AGEE,
                municipio: firstItem.municipio.nom_mun,
                colonia: firstItem.id,
              });
              setFilteredColonies([firstItem]);
            } else {
              formDireccion.setFieldsValue({
                codigo_postal: firstItem.d_codigo,
                estado: firstItem.municipio.estado.nombre_de_AGEE,
                municipio: firstItem.municipio.nom_mun,
                colonia: undefined,
              });
              setFilteredColonies(res.data.results);
            }
          } else {
            message.warn('No se encontró información relacionada al código postal brindado');
            formDireccion.resetFields();
            setFilteredColonies([]);
          }
          setLoading(false);
        }, 300);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoadingPartial);
    }
  };

  const noSeMiCodigoPostal = () => setVisibleNoSeCodigo(true);

  const onCancelNoSeCodigo = () => {
    setVisibleNoSeCodigo(false);
    setSelected();
  };

  const rules = {
    required: [{ required: true }],
    direccionStr: [{
      validator: async () => {
        if (!direccionStr) throw new Error('El campo es requerido');
      },
    }],
    entre_calle: [
      {
        validator: async (_, value) => {
          if (!value && !!formDireccion.getFieldValue('tipo_de_vialidad_entre')) {
            throw new Error('El campo es requerido');
          }
        },
      },
    ],
    tipo_de_vialidad_entre: [
      {
        validator: async (_, value) => {
          if (!value && !!formDireccion.getFieldValue('entre_calle')) {
            throw new Error('El campo es requerido');
          }
        },
      },
    ],
    y_calle: [
      {
        validator: async (_, value) => {
          if (!value && !!formDireccion.getFieldValue('tipo_de_vialidad_y_calle')) {
            throw new Error('El campo es requerido');
          }
        },
      },
    ],
    tipo_de_vialidad_y_calle: [
      {
        validator: async (_, value) => {
          if (!value && !!formDireccion.getFieldValue('y_calle')) {
            throw new Error('El campo es requerido');
          }
        },
      },
    ],
  };

  const onFinishSearch = (record) => {
    const values = record || selected;
    if (values) {
      formDireccion.setFieldsValue({
        estado: values.municipio.estado.nombre_de_AGEE,
        municipio: values.municipio.nom_mun,
        colonia: values.id,
        codigo_postal: values.d_codigo,
      });
      setFilteredColonies([values]);
      onCancelNoSeCodigo();
    } else {
      message.warn('Seleccione un elemento');
    }
  };

  const cancelControl = {
    text: 'Cancelar',
    onClick: onCancel,
    icon: <ArrowLeftOutlined />,
  };

  const saveControl = {
    text: 'Guardar',
    onClick: formDireccion.submit,
    icon: <SaveOutlined />,
  };

  const backControl = {
    text: 'Regresar',
    onClick: onCancelNoSeCodigo,
    icon: <ArrowLeftOutlined />,
  };

  const saveSelectionControl = {
    text: 'Guardar',
    onClick: () => onFinishSearch(),
    icon: <SaveOutlined />,
  };

  const getControls = () => {
    if (visibleNoSeCodigo) {
      return [backControl, saveSelectionControl];
    }
    return [cancelControl, saveControl];
  };

  return (
    <>
      {permission.view && (
        <Col style={{ display: 'flex', flex: 1 }}>
          {required ? (
            <Form.Item label="Dirección" name="direccionStr" rules={rules} style={{ width: '100%' }}>
              <Input className="transparent-input" style={{ width: '100%' }} disabled />
            </Form.Item>
          ) : (
            <Text style={{ paddingLeft: 3 }}>
              {direccionStr || (loading ? 'Obteniendo dirección' : 'Sin dirección')}
            </Text>
          )}
        </Col>
      )}
      {((!direccionStr && permission.add) || (direccionStr && permission.change)) && (
        <Col style={{ display: 'flex', flex: 1 }}>
          <Button
            ref={_ref}
            disabled={disabled}
            onClick={() => {
              if (typeof onClick === 'function') onClick();
              setVisibleDireccion(true);
            }}
            className="primary-on-hover"
            loading={loadingPartial}
            style={{ marginTop: 22 }}
            id="btn-direccion"
          >
            {!loadingPartial && <EnvironmentOutlined style={{ marginRight: 5 }} />}
            {`${direccionStr ? 'Editar' : 'Agregar'} Dirección`}
          </Button>
        </Col>
      )}
      {!parentID && (<Form.Item name={addressName} hidden><Input /></Form.Item>)}
      <Modal
        width="90%"
        visible={visibleDireccion}
        closable={false}
        maskClosable={false}
        keyboard={!loading}
        footer={null}
        title={(
          <FormSubmitCustomControls
            label={`${direccionStr ? 'Editar' : 'Agregar'} Dirección`}
            controls={getControls()}
            loading={loading}
          />
        )}
        onCancel={onCancel}
        forceRender
        centered
      >
        <Spin tip="Cargando..." spinning={loading || loadingPartial}>
          {!visibleNoSeCodigo ? (
            <Form
              form={formDireccion}
              layout="vertical"
              name="form_direccion"
              initialValues={{ pais: 152 }}
              onFinish={() => onFinish()}
              onValuesChange={async (changedValues) => {
                if (changedValues.codigo_postal) {
                  getFromPostalCode(changedValues.codigo_postal);
                }
              }}
            >
              <Row gutter={10}>
                <Col span={24}>
                  <Form.Item
                    label={(
                      <>
                        <span style={{ marginRight: 5 }}>
                          Código Postal
                        </span>
                        <Tooltip
                          title="Ingresar este campo rellenara
                            automáticamente estado, municipio y las colonias disponibles"
                        >
                          <QuestionCircleOutlined className="primary-on-hover" />
                        </Tooltip>
                      </>
                    )}
                  >
                    <Input.Group compact>
                      <Col xs={24} sm={24} md={8}>
                        <NumericInput
                          name="codigo_postal"
                          maxLength={6}
                        />
                      </Col>
                      <Form.Item noStyle>
                        <Button
                          type="link"
                          onClick={noSeMiCodigoPostal}
                        >
                          No sé mi código
                        </Button>
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="pais"
                    label="País"
                  >
                    <Select
                      disabled
                      onClear={() => {
                        setPaisTemp({ results: [] });
                      }}
                      usingExternalDataSource={Boolean(paisTemp.results.length)}
                      dataSource={paisTemp.results.length
                        ? paisTemp : paises.current}
                      pagination={{
                        getter: getCatalogoDePaises,
                        setter: (dataSource) => {
                          paises.current = dataSource;
                        },
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="estado"
                    label="Estado"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="municipio"
                    label="Municipio"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    label="Colonia"
                    name="colonia"
                    rules={rules.required}
                  >
                    <Select
                      dataSource={filteredColonies}
                      labelProp="d_asenta"
                      pagination={{
                        getter: getCodigosPostales,
                        setter: (dataSource) => {
                          setFilteredColonies(dataSource);
                        },
                        params,
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="numero_exterior"
                    label="Número Exterior"
                    rules={rules.required}
                  >
                    <Input maxLength={5} allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="numero_interior"
                    label="Número Interior"
                  >
                    <Input maxLength={5} allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8}>
                  <Form.Item>
                    <Input.Group style={{ display: 'flex' }} className="select-input" compact>
                      <Form.Item
                        name="tipo_de_vialidad"
                        rules={rules.required}
                        label="Tipo"
                        style={{ width: 150 }}
                      >
                        <Select dataSource={tiposVialidades} />
                      </Form.Item>
                      <Form.Item
                        name="calle_principal"
                        label="Vialidad Principal"
                        style={{ width: 'calc(100% - 150px)' }}
                        rules={rules.required}
                      >
                        <Input maxLength={170} allowClear />
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8}>
                  <Form.Item>
                    <Input.Group style={{ display: 'flex' }} className="select-input" compact>
                      <Form.Item
                        name="tipo_de_vialidad_entre"
                        label="Tipo"
                        rules={rules.tipo_de_vialidad_entre}
                        style={{ width: 150 }}
                      >
                        <Select dataSource={tiposVialidades} />
                      </Form.Item>
                      <Form.Item
                        name="entre_calle"
                        rules={rules.entre_calle}
                        label="Entre Vialidad"
                        style={{ width: 'calc(100% - 150px)' }}
                      >
                        <Input maxLength={170} allowClear />
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8}>
                  <Form.Item>
                    <Input.Group style={{ display: 'flex' }} className="select-input" compact>
                      <Form.Item
                        name="tipo_de_vialidad_y_calle"
                        label="Tipo"
                        rules={rules.tipo_de_vialidad_y_calle}
                        style={{ width: 150 }}
                      >
                        <Select dataSource={tiposVialidades} />
                      </Form.Item>
                      <Form.Item
                        name="y_calle"
                        rules={rules.y_calle}
                        label="Y Vialidad"
                        style={{ width: 'calc(100% - 150px)' }}
                      >
                        <Input maxLength={170} allowClear />
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="cp_texto"
                    label="Código Postal (Texto)"
                  >
                    <Input
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="colonia_texto"
                    label="Colonia (Texto)"
                  >
                    <Input
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="municipio_texto"
                    label="Municipio (Texto)"
                  >
                    <Input
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="estado_texto"
                    label="Estado (Texto)"
                  >
                    <Input
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="agencia"
                    label="Agencia"
                  >
                    <Input
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={16} lg={16} xl={12}>
                  <Form.Item
                    name="descripcion_de_la_ubicacion"
                    label="Descripción de la Ubicación"
                  >
                    <TextArea autoSize={{ minRows: 2, maxRows: 3 }} maxLength={250} />
                  </Form.Item>
                </Col>
                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Row>
            </Form>
          ) : (
            <BusquedaCodigoPostal
              setLoading={setLoading}
              callback={onFinishSearch}
              setVisible={setVisibleNoSeCodigo}
              visible={visibleNoSeCodigo}
              addressPropName="direccion"
              modal
            />
          )}
        </Spin>
      </Modal>
    </>
  );
};

ModalDireccion.propTypes = {
  parentID: PropTypes.number,
  disabled: PropTypes.bool,
  callback: PropTypes.func,
  currentURI: PropTypes.string.isRequired,
  form: PropTypes.shape(),
  onClick: PropTypes.func,
  _ref: PropTypes.shape(),
  addressName: PropTypes.oneOf(['direcciones', 'direccion', 'direccion_de_ubicacion']),
  required: PropTypes.bool,
  isEditing: PropTypes.bool,
};

ModalDireccion.defaultProps = {
  isEditing: false,
  parentID: null,
  disabled: false,
  callback: () => { },
  onClick: () => { },
  _ref: null,
  addressName: 'direccion_de_ubicacion',
  required: false,
  form: null,
};

export default ModalDireccion;
